import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { User, Workday } from '../../api/types';
import { Modal } from '../../components/Modal';
import { MapPreview } from '../../components/MapPreview';
import { useToast } from '../../components/Toast';
import { AsyncButton } from '../../components/AsyncButton';
import { fmtDateISO, fmtHM, fmtTime, todayISO, ymdLocal } from '../../utils/time';
import { computeDurationsClient } from '../../utils/workday';

function toLocalDateTime(iso: string | null) {
  if (!iso) return { date: '', time: '' };

  const d = new Date(iso);

  if (!Number.isFinite(d.getTime())) {
    return { date: '', time: '' };
  }

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');

  return {
    date: `${yyyy}-${mm}-${dd}`,
    time: `${hh}:${mi}`
  };
}

function fromLocalDateTime(date: string, time: string) {
  if (!date || !time) return null;

  const d = new Date(`${date}T${time}:00`);

  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function addDaysISO(dateISO: string, days = 1) {
  const d = new Date(`${dateISO}T00:00:00`);

  d.setDate(d.getDate() + days);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

function workDateToYMD(workDate: string) {
  return ymdLocal(workDate);
}

function timeValueToISO(workDate: string, time: string) {
  return fromLocalDateTime(workDate, time);
}

export function AdminWorkdaysPage() {
  const { push } = useToast();
  const [params] = useSearchParams();
  const initialWorkdayId = params.get('workdayId');

  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState<string>('');

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });

  const [to, setTo] = useState(() => todayISO());

  const [items, setItems] = useState<Workday[]>([]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Workday | null>(null);

  const [swDate, setSwDate] = useState('');
  const [swTime, setSwTime] = useState('');
  const [sbDate, setSbDate] = useState('');
  const [sbTime, setSbTime] = useState('');
  const [ebDate, setEbDate] = useState('');
  const [ebTime, setEbTime] = useState('');
  const [ewDate, setEwDate] = useState('');
  const [ewTime, setEwTime] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createDate, setCreateDate] = useState(() => todayISO());
  const [createStartWorkTime, setCreateStartWorkTime] = useState('');
  const [createStartBreakTime, setCreateStartBreakTime] = useState('');
  const [createEndBreakTime, setCreateEndBreakTime] = useState('');
  const [createEndWorkTime, setCreateEndWorkTime] = useState('');

  async function loadUsers() {
    const r = await api.get<{ users: User[] }>('/api/admin/users');

    setUsers(r.users);

    if (!userId && r.users[0]) {
      setUserId(r.users[0]._id);
    }

    if (!createUserId && r.users[0]) {
      setCreateUserId(r.users[0]._id);
    }
  }

  async function loadWorkdaysFor(nextUserId = userId, nextFrom = from, nextTo = to) {
    if (!nextUserId) return [];

    const r = await api.get<{ items: Workday[] }>(
      `/api/admin/users/${nextUserId}/workdays?from=${nextFrom}&to=${nextTo}`
    );

    setItems(r.items);

    return r.items;
  }

  async function loadWorkdays() {
    await loadWorkdaysFor();
  }

  useEffect(() => {
    loadUsers().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadWorkdays().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!initialWorkdayId) return;

    (async () => {
      try {
        const r = await api.get<{ items: Workday[] }>(
          `/api/admin/search?q=${initialWorkdayId}`
        );

        const w = r.items.find((x) => x._id === initialWorkdayId);

        if (w) openEdit(w);
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialWorkdayId]);

  function openEdit(w: Workday) {
    setSelected(w);

    const a = toLocalDateTime(w.start_work_at);
    const b = toLocalDateTime(w.start_break_at);
    const c = toLocalDateTime(w.end_break_at);
    const d = toLocalDateTime(w.end_work_at);

    setSwDate(a.date);
    setSwTime(a.time);

    setSbDate(b.date);
    setSbTime(b.time);

    setEbDate(c.date);
    setEbTime(c.time);

    setEwDate(d.date);
    setEwTime(d.time);

    setOpen(true);
  }

  async function save() {
    if (!selected) return;

    try {
      const patch: any = {
        start_work_at: fromLocalDateTime(swDate, swTime),
        start_break_at: fromLocalDateTime(sbDate, sbTime),
        end_break_at: fromLocalDateTime(ebDate, ebTime),
        end_work_at: fromLocalDateTime(ewDate, ewTime),
      };

      const r = await api.patch<{ workday: Workday }>(
        `/api/admin/workdays/${selected._id}`,
        patch
      );

      push({
        title: 'Timbratura aggiornata'
      });

      setOpen(false);

      await loadWorkdays();

      setSelected(r.workday);
    } catch (e: any) {
      push({
        title: 'Errore salvataggio',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  function openCreate() {
    const defaultUserId = userId || users[0]?._id || '';

    setCreateUserId(defaultUserId);
    setCreateDate(todayISO());
    setCreateStartWorkTime('');
    setCreateStartBreakTime('');
    setCreateEndBreakTime('');
    setCreateEndWorkTime('');
    setCreateOpen(true);
  }

  const duplicateInLoadedItems = useMemo(() => {
    if (!createUserId || !createDate) return false;

    if (createUserId !== userId) return false;

    return items.some((w) => workDateToYMD(w.work_date) === createDate);
  }, [createUserId, createDate, userId, items]);

  async function checkExistingWorkday(nextUserId: string, nextDate: string) {
    const nextTo = addDaysISO(nextDate);

    const r = await api.get<{ items: Workday[] }>(
      `/api/admin/users/${nextUserId}/workdays?from=${nextDate}&to=${nextTo}`
    );

    return r.items.find((w) => workDateToYMD(w.work_date) === nextDate) || null;
  }

  function validateCreate() {
    if (!createUserId) return 'Seleziona un dipendente';
    if (!createDate) return 'Seleziona il giorno della timbratura';
    if (!createStartWorkTime) return 'Inserisci almeno l’orario di inizio lavoro';

    const sw = timeValueToISO(createDate, createStartWorkTime);
    const sb = timeValueToISO(createDate, createStartBreakTime);
    const eb = timeValueToISO(createDate, createEndBreakTime);
    const ew = timeValueToISO(createDate, createEndWorkTime);

    if (!sw) return 'Inizio lavoro non valido';

    if ((sb && !eb) || (!sb && eb)) {
      return 'Per la pausa devi indicare sia inizio pausa sia fine pausa';
    }

    const swMs = new Date(sw).getTime();
    const sbMs = sb ? new Date(sb).getTime() : null;
    const ebMs = eb ? new Date(eb).getTime() : null;
    const ewMs = ew ? new Date(ew).getTime() : null;

    if (sbMs !== null && sbMs <= swMs) {
      return 'L’inizio pausa deve essere successivo all’inizio lavoro';
    }

    if (sbMs !== null && ebMs !== null && ebMs <= sbMs) {
      return 'La fine pausa deve essere successiva all’inizio pausa';
    }

    if (ewMs !== null && ewMs <= swMs) {
      return 'La fine lavoro deve essere successiva all’inizio lavoro';
    }

    if (ewMs !== null && sbMs !== null && sbMs >= ewMs) {
      return 'La pausa deve essere compresa tra inizio e fine lavoro';
    }

    if (ewMs !== null && ebMs !== null && ebMs > ewMs) {
      return 'La fine pausa non può essere successiva alla fine lavoro';
    }

    return null;
  }

  async function saveCreate() {
    try {
      const validationError = validateCreate();

      if (validationError) {
        push({
          title: 'Dati non validi',
          body: validationError
        });

        return;
      }

      const uid = createUserId;
      const workDate = createDate;

      const existing = await checkExistingWorkday(uid, workDate);

      if (existing) {
        push({
          title: 'Timbratura già presente',
          body: 'Per questo dipendente esiste già una timbratura in questo giorno.'
        });

        return;
      }

      const r = await api.post<{ workday: Workday }>(
        `/api/admin/users/${uid}/workdays`,
        {
          work_date: workDate,
          start_work_at: timeValueToISO(workDate, createStartWorkTime),
          start_break_at: timeValueToISO(workDate, createStartBreakTime),
          end_break_at: timeValueToISO(workDate, createEndBreakTime),
          end_work_at: timeValueToISO(workDate, createEndWorkTime)
        }
      );

      push({
        title: 'Timbratura aggiunta'
      });

      setCreateOpen(false);

      const nextFrom = workDate;
      const nextTo = addDaysISO(workDate);

      setUserId(uid);
      setFrom(nextFrom);
      setTo(nextTo);

      await loadWorkdaysFor(uid, nextFrom, nextTo);

      openEdit(r.workday);
    } catch (e: any) {
      push({
        title: 'Errore creazione',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  const rows = useMemo(() => {
    return items.map((w) => {
      const dur = computeDurationsClient(w, new Date());

      return {
        w,
        dur
      };
    });
  }, [items]);

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="h2" style={{ marginTop: 0 }}>
          Timbrature
        </div>

        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div>
            <div className="small">
              Dipendente
            </div>

            <select
              className="input"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="small">
              Da
            </div>

            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div>
            <div className="small">
              A
            </div>

            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        <div className="spread" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <AsyncButton
            className="btn btnPrimary"
            onClickAsync={loadWorkdays}
            busyText="Carico…"
          >
            Cerca
          </AsyncButton>

          <button
            className="btn btnSecondary"
            type="button"
            disabled={users.length === 0}
            onClick={openCreate}
          >
            + Aggiungi manuale
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--black)', color: 'var(--white)' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>
                  Data
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Inizio
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Fine
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Netto
                </th>

                <th style={{ padding: 12 }} />
              </tr>
            </thead>

            <tbody>
              {rows.map(({ w, dur }, idx) => (
                <tr
                  key={w._id}
                  style={{
                    background: idx % 2
                      ? 'rgba(255,255,255,.02)'
                      : 'rgba(255,255,255,.04)'
                  }}
                >
                  <td style={{ padding: 12 }}>
                    {fmtDateISO(w.work_date)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {fmtTime(w.start_work_at)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {fmtTime(w.end_work_at)}
                  </td>

                  <td style={{ padding: 12, fontWeight: 900 }}>
                    {fmtHM(dur.netMin)}
                  </td>

                  <td style={{ padding: 12, textAlign: 'right' }}>
                    <button
                      className="btn btnGhost"
                      type="button"
                      onClick={() => openEdit(w)}
                    >
                      Apri
                    </button>
                  </td>
                </tr>
              ))}

              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="small" style={{ padding: 16 }}>
                    Nessuna timbratura nel periodo.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={selected ? `Dettagli e modifica · ${fmtDateISO(selected.work_date)}` : 'Dettagli'}
      >
        {selected ? (
          <div style={{ display: 'grid', gap: 14 }}>
            <div className="small">
              Date e orari sono separati per rendere più chiara la modifica.
              Le posizioni sono mostrate sotto ogni evento.
            </div>

            <div className="card" style={{ background: 'var(--gray-200)', boxShadow: 'none' }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  Netto (stima live)
                </span>

                <b>
                  {fmtHM(computeDurationsClient(selected, new Date()).netMin)}
                </b>
              </div>
            </div>

            <div className="grid" style={{ gap: 12 }}>
              <EventRow
                label="Inizio lavoro"
                date={swDate}
                time={swTime}
                onDate={setSwDate}
                onTime={setSwTime}
                address={selected.start_work_loc?.address}
                map={selected.start_work_loc ? <MapPreview loc={selected.start_work_loc} /> : null}
              />

              <EventRow
                label="Inizio pausa"
                date={sbDate}
                time={sbTime}
                onDate={setSbDate}
                onTime={setSbTime}
                address={selected.start_break_loc?.address}
                map={selected.start_break_loc ? <MapPreview loc={selected.start_break_loc} /> : null}
              />

              <EventRow
                label="Fine pausa"
                date={ebDate}
                time={ebTime}
                onDate={setEbDate}
                onTime={setEbTime}
                address={selected.end_break_loc?.address}
                map={selected.end_break_loc ? <MapPreview loc={selected.end_break_loc} /> : null}
              />

              <EventRow
                label="Fine lavoro"
                date={ewDate}
                time={ewTime}
                onDate={setEwDate}
                onTime={setEwTime}
                address={selected.end_work_loc?.address}
                map={selected.end_work_loc ? <MapPreview loc={selected.end_work_loc} /> : null}
              />
            </div>

            <div className="spread">
              <button
                className="btn btnSecondary"
                type="button"
                onClick={() => setOpen(false)}
              >
                Annulla
              </button>

              <AsyncButton
                className="btn btnPrimary"
                onClickAsync={save}
                busyText="Salvo…"
              >
                Salva
              </AsyncButton>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Aggiungi timbratura manuale"
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="small">
            Scegli dipendente e giorno. Se per quel dipendente esiste già una timbratura
            in quel giorno, il sistema non permette di crearne un’altra.
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <div>
              <div className="small">
                Dipendente
              </div>

              <select
                className="input"
                value={createUserId}
                onChange={(e) => setCreateUserId(e.target.value)}
              >
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.first_name} {u.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="small">
                Giorno
              </div>

              <input
                className="input"
                type="date"
                value={createDate}
                onChange={(e) => setCreateDate(e.target.value)}
              />
            </div>
          </div>

          {duplicateInLoadedItems ? (
            <div
              className="card"
              style={{
                background: 'rgba(255, 180, 0, .12)',
                boxShadow: 'none',
                border: '1px solid rgba(255, 180, 0, .35)'
              }}
            >
              <b>
                Timbratura già presente
              </b>

              <div className="small" style={{ marginTop: 4 }}>
                Nella lista caricata esiste già una timbratura per questo dipendente in questo giorno.
              </div>
            </div>
          ) : null}

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <ManualTimeField
              label="Inizio lavoro"
              required
              value={createStartWorkTime}
              onChange={setCreateStartWorkTime}
            />

            <ManualTimeField
              label="Inizio pausa"
              value={createStartBreakTime}
              onChange={setCreateStartBreakTime}
            />

            <ManualTimeField
              label="Fine pausa"
              value={createEndBreakTime}
              onChange={setCreateEndBreakTime}
            />

            <ManualTimeField
              label="Fine lavoro"
              value={createEndWorkTime}
              onChange={setCreateEndWorkTime}
            />
          </div>

          <div className="spread">
            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => setCreateOpen(false)}
            >
              Annulla
            </button>

            <AsyncButton
              className="btn btnPrimary"
              onClickAsync={saveCreate}
              busyText="Creo…"
              disabled={duplicateInLoadedItems || !createUserId || !createDate || !createStartWorkTime}
            >
              Aggiungi timbratura
            </AsyncButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function EventRow(props: {
  label: string;
  date: string;
  time: string;
  onDate: (v: string) => void;
  onTime: (v: string) => void;
  address?: string;
  map?: React.ReactNode;
}) {
  return (
    <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 900 }}>
            {props.label}
          </div>

          <div className="small" style={{ marginTop: 4 }}>
            {props.address || 'Posizione: —'}
          </div>
        </div>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div>
            <div className="small">
              Data
            </div>

            <input
              className="input"
              type="date"
              value={props.date}
              onChange={(e) => props.onDate(e.target.value)}
              style={{ minWidth: 160 }}
            />
          </div>

          <div>
            <div className="small">
              Ora
            </div>

            <input
              className="input"
              type="time"
              value={props.time}
              onChange={(e) => props.onTime(e.target.value)}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      </div>

      {props.map ? (
        <div style={{ marginTop: 10 }}>
          {props.map}
        </div>
      ) : null}
    </div>
  );
}

function ManualTimeField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 900 }}>
        {props.label}
        {props.required ? ' *' : ''}
      </div>

      <div className="small" style={{ marginTop: 4 }}>
        Ora
      </div>

      <input
        className="input"
        type="time"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        style={{ marginTop: 6 }}
      />
    </div>
  );
}